import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';

const emptyItem = { description: '', quantity: 1, unit_price: '' };

export default function ProposalBuilder() {
     const { inquiryId, id: proposalId } = useParams();
     const navigate = useNavigate();
     const [inquiry, setInquiry] = useState(null);
     const [form, setForm] = useState({
          customer_name: '',
          customer_email: '',
          line_items: [{ ...emptyItem }],
          tax_percent: 0,
          valid_until: '',
          notes: '',
     });
     const [status, setStatus] = useState('draft');
     const [saving, setSaving] = useState(false);
     const [sending, setSending] = useState(false);
     const [loading, setLoading] = useState(true);

     useEffect(() => {
          loadData();
          // eslint-disable-next-line react-hooks/exhaustive-deps
     }, []);

     async function loadData() {
          if (proposalId) {
               // Editing existing proposal
               const { data } = await supabase
                    .from('proposals')
                    .select('*')
                    .eq('id', proposalId)
                    .single();
               if (data) {
                    const items = (data.line_items || []).map((li) => ({
                         description: li.description,
                         quantity: li.quantity,
                         unit_price: (li.unit_price_cents / 100).toFixed(2),
                    }));
                    setForm({
                         customer_name: data.customer_name,
                         customer_email: data.customer_email,
                         line_items: items.length > 0 ? items : [{ ...emptyItem }],
                         tax_percent: data.subtotal_cents > 0 ? ((data.tax_cents / data.subtotal_cents) * 100) : 0,
                         valid_until: data.valid_until || '',
                         notes: data.notes || '',
                    });
                    setStatus(data.status);
                    if (data.inquiry_id) {
                         const { data: inq } = await supabase
                              .from('inquiries')
                              .select('*')
                              .eq('id', data.inquiry_id)
                              .single();
                         setInquiry(inq);
                    }
               }
          } else if (inquiryId) {
               // New proposal from inquiry
               const { data: inq } = await supabase
                    .from('inquiries')
                    .select('*')
                    .eq('id', inquiryId)
                    .single();
               if (inq) {
                    setInquiry(inq);
                    setForm((f) => ({
                         ...f,
                         customer_name: inq.name,
                         customer_email: inq.email,
                    }));
               }
          }
          setLoading(false);
     }

     function calcSubtotal() {
          return form.line_items.reduce((sum, li) => {
               return sum + Math.round((parseFloat(li.unit_price) || 0) * 100) * (parseInt(li.quantity) || 0);
          }, 0);
     }

     function calcTax() {
          return Math.round(calcSubtotal() * (form.tax_percent / 100));
     }

     function updateItem(idx, field, value) {
          const items = [...form.line_items];
          items[idx] = { ...items[idx], [field]: value };
          setForm({ ...form, line_items: items });
     }

     function addItem() {
          setForm({ ...form, line_items: [...form.line_items, { ...emptyItem }] });
     }

     function removeItem(idx) {
          if (form.line_items.length <= 1) return;
          setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });
     }

     async function generateNumber() {
          const today = format(new Date(), 'yyyyMMdd');
          const { count } = await supabase
               .from('proposals')
               .select('*', { count: 'exact', head: true })
               .like('proposal_number', `P-${today}%`);
          return `P-${today}-${String((count || 0) + 1).padStart(3, '0')}`;
     }

     async function save() {
          setSaving(true);
          const subtotal = calcSubtotal();
          const tax = calcTax();
          const lineItems = form.line_items.map((li) => ({
               description: li.description,
               quantity: parseInt(li.quantity) || 1,
               unit_price_cents: Math.round((parseFloat(li.unit_price) || 0) * 100),
               total_cents: Math.round((parseFloat(li.unit_price) || 0) * 100) * (parseInt(li.quantity) || 1),
          }));

          const payload = {
               inquiry_id: inquiry?.id || null,
               customer_name: form.customer_name,
               customer_email: form.customer_email,
               line_items: lineItems,
               subtotal_cents: subtotal,
               tax_cents: tax,
               total_cents: subtotal + tax,
               valid_until: form.valid_until || null,
               notes: form.notes || null,
               updated_at: new Date().toISOString(),
          };

          if (proposalId) {
               await supabase.from('proposals').update(payload).eq('id', proposalId);
          } else {
               payload.proposal_number = await generateNumber();
               payload.status = 'draft';
               const { data } = await supabase.from('proposals').insert(payload).select().single();
               if (data) navigate(`/admin/proposals/${data.id}/edit`, { replace: true });
          }
          setSaving(false);
     }

     async function sendProposal() {
          await save();
          setSending(true);
          const pid = proposalId;
          if (!pid) {
               setSending(false);
               return;
          }

          // Update inquiry status
          if (inquiry?.id) {
               await supabase
                    .from('inquiries')
                    .update({ status: 'proposal_sent', updated_at: new Date().toISOString() })
                    .eq('id', inquiry.id);
          }

          const { error } = await supabase.functions.invoke('send-proposal', {
               body: { proposalId: pid },
          });
          if (error) {
               alert('Error sending proposal: ' + error.message);
          } else {
               setStatus('sent');
               alert('Proposal sent!');
          }
          setSending(false);
     }

     const inputClasses =
          'bg-white border border-gray-200 rounded-lg px-4 py-2 text-darker placeholder-darker/30 focus:outline-none focus:border-yellow duration-300';

     if (loading) return <p className='text-darker/60'>Loading...</p>;

     const subtotal = calcSubtotal();
     const tax = calcTax();
     const total = subtotal + tax;

     return (
          <div>
               <div className='flex items-center gap-4 mb-6'>
                    <button
                         onClick={() => inquiry ? navigate(`/admin/inquiries/${inquiry.id}`) : navigate('/admin/inquiries')}
                         className='text-darker/40 hover:text-darker duration-300'
                    >
                         &larr; Back
                    </button>
                    <h1 className='text-darker text-2xl font-bold'>
                         {proposalId ? 'Edit Proposal' : 'New Proposal'}
                    </h1>
               </div>

               <div className='space-y-6'>
                    {/* Customer Info */}
                    <div className='bg-white rounded-xl border border-gray-200 p-6'>
                         <h2 className='text-darker font-bold mb-4'>Customer</h2>
                         <div className='grid tablet:grid-cols-2 gap-4'>
                              <input
                                   type='text'
                                   placeholder='Customer Name'
                                   value={form.customer_name}
                                   onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                                   className={inputClasses + ' w-full'}
                              />
                              <input
                                   type='email'
                                   placeholder='Customer Email'
                                   value={form.customer_email}
                                   onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                                   className={inputClasses + ' w-full'}
                              />
                         </div>
                    </div>

                    {/* Line Items */}
                    <div className='bg-white rounded-xl border border-gray-200 p-6'>
                         <h2 className='text-darker font-bold mb-4'>Line Items</h2>
                         <div className='space-y-3'>
                              {form.line_items.map((item, idx) => (
                                   <div key={idx} className='grid grid-cols-12 gap-3 items-center'>
                                        <input
                                             type='text'
                                             placeholder='Description'
                                             value={item.description}
                                             onChange={(e) => updateItem(idx, 'description', e.target.value)}
                                             className={inputClasses + ' col-span-6'}
                                        />
                                        <input
                                             type='number'
                                             placeholder='Qty'
                                             min='1'
                                             value={item.quantity}
                                             onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                             className={inputClasses + ' col-span-2'}
                                        />
                                        <input
                                             type='number'
                                             placeholder='Price'
                                             step='0.01'
                                             min='0'
                                             value={item.unit_price}
                                             onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                                             className={inputClasses + ' col-span-3'}
                                        />
                                        <button
                                             onClick={() => removeItem(idx)}
                                             className='col-span-1 text-red-400 hover:text-red-700 text-center'
                                        >
                                             &times;
                                        </button>
                                   </div>
                              ))}
                         </div>
                         <button
                              onClick={addItem}
                              className='mt-3 text-yellow text-sm hover:underline'
                         >
                              + Add Line Item
                         </button>

                         {/* Totals */}
                         <div className='mt-6 border-t border-gray-200 pt-4 space-y-2 text-sm'>
                              <div className='flex justify-between text-darker/60'>
                                   <span>Subtotal</span>
                                   <span>${(subtotal / 100).toLocaleString()}</span>
                              </div>
                              <div className='flex justify-between items-center text-darker/60'>
                                   <div className='flex items-center gap-2'>
                                        <span>Tax</span>
                                        <input
                                             type='number'
                                             step='0.1'
                                             min='0'
                                             value={form.tax_percent}
                                             onChange={(e) => setForm({ ...form, tax_percent: parseFloat(e.target.value) || 0 })}
                                             className={inputClasses + ' w-20 text-center text-sm py-1'}
                                        />
                                        <span>%</span>
                                   </div>
                                   <span>${(tax / 100).toLocaleString()}</span>
                              </div>
                              <div className='flex justify-between text-darker font-bold text-lg pt-2 border-t border-gray-200'>
                                   <span>Total</span>
                                   <span className='text-yellow'>${(total / 100).toLocaleString()}</span>
                              </div>
                         </div>
                    </div>

                    {/* Additional Info */}
                    <div className='bg-white rounded-xl border border-gray-200 p-6'>
                         <h2 className='text-darker font-bold mb-4'>Details</h2>
                         <div className='grid tablet:grid-cols-2 gap-4'>
                              <div>
                                   <label className='text-darker/40 text-sm mb-1 block'>Valid Until</label>
                                   <input
                                        type='date'
                                        value={form.valid_until}
                                        onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                                        className={inputClasses + ' w-full'}
                                   />
                              </div>
                         </div>
                         <div className='mt-4'>
                              <label className='text-darker/40 text-sm mb-1 block'>Notes / Terms</label>
                              <textarea
                                   value={form.notes}
                                   onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                   className={inputClasses + ' w-full h-24 resize-none'}
                                   placeholder='Payment terms, scope of work, etc.'
                              />
                         </div>
                    </div>

                    {/* Actions */}
                    <div className='flex gap-3'>
                         <button
                              onClick={save}
                              disabled={saving}
                              className='px-6 py-3 bg-gray-50 text-darker rounded-lg font-bold hover:bg-gray-100 duration-300 disabled:opacity-50'
                         >
                              {saving ? 'Saving...' : 'Save Draft'}
                         </button>
                         {proposalId && status === 'draft' && (
                              <button
                                   onClick={sendProposal}
                                   disabled={sending}
                                   className='px-6 py-3 bg-yellow text-black rounded-lg font-bold hover:bg-yellow/90 duration-300 disabled:opacity-50'
                              >
                                   {sending ? 'Sending...' : 'Send Proposal'}
                              </button>
                         )}
                         {status === 'sent' && (
                              <span className='px-6 py-3 bg-blue-500/20 text-blue-300 rounded-lg text-sm flex items-center'>
                                   Proposal Sent
                              </span>
                         )}
                         {status === 'accepted' && (
                              <span className='px-6 py-3 bg-green-500/20 text-green-300 rounded-lg text-sm flex items-center'>
                                   Accepted
                              </span>
                         )}
                    </div>
               </div>
          </div>
     );
}
